import React, { useMemo, useState } from 'react';
import type { AppState, RolPermiso } from '../types';
import { PageShell, glassLight, primaryButton, ghostButton, Badge, EmptyState, useIsMobile, usePagination, Pagination, T } from '../ui';
// Colores fijos para los primeros roles; el resto usa un color genérico
const ROL_COLORS = [{
  color: '#8b5cf6',
  soft: 'rgba(139,92,246,0.14)'
}, {
  color: T.info,
  soft: T.infoSoft
}, {
  color: T.accent,
  soft: T.accentSoft
}, {
  color: '#ec4899',
  soft: 'rgba(236,72,153,0.14)'
}, {
  color: '#10b981',
  soft: 'rgba(16,185,129,0.14)'
}];
interface Props {
  state: AppState;
  onNew: () => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  canArchive: boolean;
}
export function Usuarios({
  state,
  onNew,
  onEdit,
  onDelete,
  canArchive
}: Props) {
  const [tab, setTab] = useState<'activos' | 'archivados'>('activos');
  const isMobile = useIsMobile();
  // Construir etiquetas y colores dinámicamente desde los roles reales
  const rolLabel: Record<string, string> = {};
  const rolColor: Record<string, {
    color: string;
    soft: string;
  }> = {};
  state.roles.forEach((r, i) => {
    const key = r.id.replace(/^rol-/, '');
    rolLabel[key] = r.nombre;
    rolColor[key] = ROL_COLORS[i % ROL_COLORS.length];
  });
  const filteredUsuarios = useMemo(() => {
    return state.usuarios.filter(u => tab === 'activos' ? !u.archivado : u.archivado);
  }, [state.usuarios, tab]);
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(filteredUsuarios);
  const sucNamesFor = (ids?: string[]) => {
    const safeIds = Array.isArray(ids) ? ids : [];
    return safeIds.length === 0 ? <span style={{
      color: '#c4b5fd',
      fontStyle: 'italic'
    }} data-id="element-1322" data-mp="39hnc2eiad95j">
      
        Todas las sucursales
      </span> : safeIds.map(sid => state.sucs.find(x => x.id === sid)?.nombre ?? sid).join(', ');
  };
  return <PageShell title="Usuarios y acceso" sub="Gestiona quién puede entrar y qué sucursales puede ver" action={<button onClick={onNew} style={primaryButton} data-id="element-1324" data-mp="fotwzhgftdth">
          + Nuevo usuario
        </button>} data-id="element-1323" data-mp="5uwny4gzx133">
      
      <div style={{
      display: 'flex',
      gap: 8,
      marginBottom: 20
    }} data-id="element-1325" data-mp="ar0p8jzhkbr3">
        
        <button onClick={() => {
        setTab('activos');
        setPage(1);
      }} style={{
        ...ghostButton,
        background: tab === 'activos' ? T.accentSoft : 'transparent',
        color: tab === 'activos' ? T.accent : T.textMuted
      }} data-id="element-1326" data-mp="5pbd110j1s1f">
          
          Activos
        </button>
        {canArchive && <button onClick={() => {
        setTab('archivados');
        setPage(1);
      }} style={{
        ...ghostButton,
        background: tab === 'archivados' ? T.accentSoft : 'transparent',
        color: tab === 'archivados' ? T.accent : T.textMuted
      }} data-id="element-1327" data-mp="3o3aunv8uc5ma">
          
            Archivados
          </button>}
      </div>

      {filteredUsuarios.length === 0 ? <EmptyState title="Sin usuarios registrados" hint="Agrega usuarios y define a qué sucursales tienen acceso." icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" data-id="element-1329" data-mp="1knz6wixpehh6">
          
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" data-id="element-1330" data-mp="1tpyk105ll1eu" />
              <circle cx="9" cy="7" r="4" data-id="element-1331" data-mp="1ynrrdwin01gp" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" data-id="element-1332" data-mp="1yni107tojsah" />
            </svg>} data-id="element-1328" data-mp="356t77q10yrvu" /> : isMobile ? <>
          <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12
      }} data-id="element-1333" data-mp="111knkou5qd5e">
          
            {pageItems.map(u => {
          const rc = rolColor[u.rol] ?? {
            color: T.textMuted,
            soft: 'rgba(255,255,255,0.06)'
          };
          return <div key={u.id} style={{
            ...glassLight,
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 12
          }} data-id="element-1334" data-mp="1fq7dtllbopbe">
                
                  <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 10
            }} data-id="element-1335" data-mp="n7jxejh9tygn">
                  
                    <div style={{
                minWidth: 0
              }} data-id="element-1336" data-mp="2ky068rwom8l4">
                    
                      <div style={{
                  fontWeight: 700,
                  color: T.text,
                  fontSize: 15
                }} data-id="element-1337" data-mp="2ypeogwirm5r4">
                      
                        {u.nombre}
                      </div>
                      <div style={{
                  fontSize: 13,
                  color: T.textSecondary,
                  marginTop: 2,
                  wordBreak: 'break-all'
                }} data-id="element-1338" data-mp="33mx1oz3tf74x">
                      
                        {u.correo}
                      </div>
                    </div>
                    <div style={{
                flexShrink: 0
              }} data-id="element-1339" data-mp="1n857cgn3zpsz">
                    
                      <Badge label={rolLabel[u.rol]} color={rc.color} soft={rc.soft} data-id="element-1340" data-mp="1ia3nvulrtogz" />
                    
                    </div>
                  </div>
                  <div data-id="element-1341" data-mp="3tz0fiscbjfjy">
                    <div style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                color: T.textMuted,
                marginBottom: 4
              }} data-id="element-1342" data-mp="2qcuyelwjwdg">
                    
                      Sucursales asignadas
                    </div>
                    <div style={{
                fontSize: 13,
                color: T.textSecondary,
                lineHeight: 1.45
              }} data-id="element-1343" data-mp="3b4uyq44t8pr9">
                    
                      {sucNamesFor(u.sucursalesIds)}
                    </div>
                  </div>
                  <div style={{
              display: 'flex',
              gap: 8,
              paddingTop: 4,
              borderTop: `1px solid ${T.border}`
            }} data-id="element-1344" data-mp="368e7v2687xah">
                  
                    <button className="getr-btn-ghost" onClick={() => onEdit(u.id)} style={{
                ...ghostButton,
                flex: 1,
                marginTop: 8
              }} data-id="element-1345" data-mp="14eyie8lvkoh3">
                    
                      Editar
                    </button>
                    {u.rol !== 'admin' && <button onClick={() => onDelete(u.id)} style={{
                ...ghostButton,
                flex: 1,
                marginTop: 8,
                color: u.archivado ? T.info : T.warn,
                borderColor: u.archivado ? 'rgba(96,165,250,0.3)' : 'rgba(251,191,36,0.3)',
                background: u.archivado ? 'rgba(96,165,250,0.1)' : 'rgba(251,191,36,0.1)'
              }} data-id="element-1346" data-mp="24d9sf16q1iv4">
                    
                        {u.archivado ? 'Desarchivar' : 'Archivar'}
                      </button>}
                  </div>
                </div>;
        })}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="usuarios" data-id="element-1347" data-mp="3fsgnt989cruc" />
        
        </> : <div style={{
      ...glassLight,
      overflow: 'hidden'
    }} data-id="element-1348" data-mp="3ic38nwvhjnva">
        
          <div style={{
        overflowX: 'auto'
      }} data-id="element-1349" data-mp="3ie6631yywja1">
          
            <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 13
        }} data-id="element-1350" data-mp="3nb6g1nyxu66f">
            
              <thead data-id="element-1351" data-mp="5yozxpao78e">
                <tr style={{
              background: T.surfaceSubtle
            }} data-id="element-1352" data-mp="6gr3ayn1fo8">
                
                  {['Usuario', 'Correo', 'Rol', 'Sucursales asignadas', 'Acciones'].map(h => <th key={h} style={{
                padding: '13px 16px',
                fontWeight: 700,
                fontSize: 10,
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                color: T.textMuted,
                textAlign: 'left',
                borderBottom: `1px solid ${T.border}`
              }} data-id="element-1353" data-mp="a44q4d3a19c7">
                  
                      {h}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-1354" data-mp="ssjcuo6ka177">
                {pageItems.map((u, i) => {
              const uSucIds = Array.isArray(u.sucursalesIds) ? u.sucursalesIds : [];
              const sucNames = uSucIds.length === 0 ? <span style={{
                color: '#c4b5fd',
                fontStyle: 'italic'
              }} data-id="element-1355" data-mp="198u6ske83v7t">
                  
                        Todas las sucursales
                      </span> : uSucIds.map(sid => {
                const s = state.sucs.find(x => x.id === sid);
                return s ? s.nombre : sid;
              }).join(', ');
              const rc = rolColor[u.rol] ?? {
                color: T.textMuted,
                soft: 'rgba(255,255,255,0.06)'
              };
              return <tr key={u.id} className="getr-row" style={{
                background: i % 2 === 1 ? T.surface : 'transparent',
                borderBottom: i < pageItems.length - 1 ? `1px solid ${T.border}` : 'none',
                transition: 'background .15s'
              }} data-id="element-1356" data-mp="10efr4gps6io6" data-mpi={i}>
                    
                      <td style={{
                  padding: '13px 16px',
                  fontWeight: 600,
                  color: T.text
                }} data-id="element-1357" data-mp="2jcluly9bvwrg">
                      
                        {u.nombre}
                      </td>
                      <td style={{
                  padding: '13px 16px',
                  color: T.textSecondary
                }} data-id="element-1358" data-mp="29giqwnytvq2e">
                      
                        {u.correo}
                      </td>
                      <td style={{
                  padding: '13px 16px'
                }} data-id="element-1359" data-mp="321sh7wd8t8ij">
                      
                        <Badge label={rolLabel[u.rol]} color={rc.color} soft={rc.soft} data-id="element-1360" data-mp="2t2x8acitdkbj" />
                      
                      </td>
                      <td style={{
                  padding: '13px 16px',
                  color: T.textSecondary,
                  maxWidth: 260
                }} data-id="element-1361" data-mp="2x3h49crnihw3">
                      
                        {sucNames}
                      </td>
                      <td style={{
                  padding: '13px 16px'
                }} data-id="element-1362" data-mp="1b7znkq2bz0j3">
                      
                        <div style={{
                    display: 'flex',
                    gap: 6
                  }} data-id="element-1363" data-mp="1g7m1z2twamth">
                        
                          <button className="getr-btn-ghost" onClick={() => onEdit(u.id)} style={ghostButton} data-id="element-1364" data-mp="12fflffmtydti">
                          
                            Editar
                          </button>
                          {u.rol !== 'admin' && <button onClick={() => onDelete(u.id)} style={{
                      ...ghostButton,
                      color: u.archivado ? T.info : T.warn,
                      borderColor: u.archivado ? 'rgba(96,165,250,0.3)' : 'rgba(251,191,36,0.3)',
                      background: u.archivado ? 'rgba(96,165,250,0.1)' : 'rgba(251,191,36,0.1)'
                    }} data-id="element-1365" data-mp="xgl64no2n7e5">
                          
                              {u.archivado ? 'Desarchivar' : 'Archivar'}
                            </button>}
                        </div>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>
          <div style={{
        padding: '4px 16px 14px'
      }} data-id="element-1366" data-mp="25eiyxsk26lc">
          
            <Pagination page={page} totalPages={totalPages} onChange={setPage} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="usuarios" data-id="element-1367" data-mp="g0gxg9bgx49r" />
          
          </div>
        </div>}

    </PageShell>;
}